import type { ApiResponse, CourtHireBooking, CourtHireRate, ClubClosure, PaginatedResponse } from '@court-sync/shared';
import type { ClubOperatingHourSlot } from '@court-sync/shared';
import type { CourtHireAvailabilityGrid } from '@/lib/courtHirePrefill';
import { apiDelete, apiGet, apiPatch, apiPost, publicApiClient, publicApiGet } from './apiClient';

export interface PublicCourtHireCourtOption {
  id: string;
  name: string;
}

export interface PublicCourtHireAvailability {
  clubId: string;
  courts: PublicCourtHireCourtOption[];
  rates: CourtHireRate[];
  bookings: CourtHireBooking[];
  operatingHours: ClubOperatingHourSlot[];
  closures: ClubClosure[];
}

export interface GuestCourtHireBookingPayload {
  courtId: string;
  startAt: string;
  endAt: string;
  guestEmail: string;
  guestFirstName: string;
  guestLastName: string;
  guestPhone: string;
  rateId?: string;
  addons?: Array<{ itemId: string; quantity: number }>;
}

export interface GuestCourtHireBookingResult {
  bookingId: string;
  paymentRequired?: boolean;
  status?: 'pending' | 'confirmed';
  clientSecret?: string;
  paymentIntentId?: string;
  stripeConnectAccountId?: string;
  subtotalCents?: number;
  taxAmountCents?: number;
  taxInclusive?: boolean;
  totalPriceCents?: number;
  discountPercent?: number;
}

export interface PublicInventoryAddon {
  id: string;
  name: string;
  category: string | null;
  hirePriceCents: number;
  currency: string;
  availableQuantity: number;
  estimatedPriceCents: number;
}

export const courtHireService = {
  async getRates(clubId: string): Promise<ApiResponse<CourtHireRate[]>> {
    return apiGet<CourtHireRate[]>(`/court-hire/${clubId}/rates`);
  },

  async createRate(
    clubId: string,
    data: Partial<CourtHireRate>
  ): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/court-hire/${clubId}/rates`, data);
  },

  async updateRate(
    clubId: string,
    rateId: string,
    data: Partial<CourtHireRate>
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/court-hire/${clubId}/rates/${rateId}`, data);
  },

  async deactivateRate(clubId: string, rateId: string): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/court-hire/${clubId}/rates/${rateId}`, { isActive: false });
  },

  async deleteRate(clubId: string, rateId: string): Promise<ApiResponse<null>> {
    return apiDelete<null>(`/court-hire/${clubId}/rates/${rateId}`);
  },

  async getBookings(
    clubId: string,
    filters?: {
      from?: string;
      to?: string;
      courtId?: string;
      status?: CourtHireBooking['status'];
      search?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<CourtHireBooking[]> | PaginatedResponse<CourtHireBooking>> {
    return apiGet<CourtHireBooking[]>(`/court-hire/${clubId}/bookings`, { params: filters });
  },

  async updateBookingStatus(
    clubId: string,
    bookingId: string,
    status: CourtHireBooking['status']
  ): Promise<ApiResponse<null>> {
    return apiPatch<null>(`/court-hire/${clubId}/bookings/${bookingId}`, { status });
  },

  async getPublicAvailability(
    clubSlug: string,
    params?: { courtId?: string; from?: string; to?: string }
  ): Promise<ApiResponse<PublicCourtHireAvailability>> {
    return publicApiGet<PublicCourtHireAvailability>(`/public/court-hire/${clubSlug}/availability`, {
      params,
    });
  },

  async getPublicAvailabilityGrid(
    clubSlug: string,
    query: { date: string; sportId?: string; locationId?: string }
  ): Promise<ApiResponse<CourtHireAvailabilityGrid>> {
    return publicApiGet<CourtHireAvailabilityGrid>(`/public/court-hire/${clubSlug}/availability`, {
      params: query,
    });
  },

  async getPublicAddonCatalog(
    clubSlug: string
  ): Promise<ApiResponse<{ hasHireableEquipment: boolean }>> {
    return publicApiGet<{ hasHireableEquipment: boolean }>(
      `/public/court-hire/${clubSlug}/addons/catalog`
    );
  },

  async getPublicAddons(
    clubSlug: string,
    params: { startAt: string; endAt: string }
  ): Promise<ApiResponse<PublicInventoryAddon[]>> {
    return publicApiGet<PublicInventoryAddon[]>(`/public/court-hire/${clubSlug}/addons`, {
      params,
    });
  },

  async createGuestBooking(
    clubSlug: string,
    payload: GuestCourtHireBookingPayload
  ): Promise<ApiResponse<GuestCourtHireBookingResult>> {
    const response = await publicApiClient.post<ApiResponse<GuestCourtHireBookingResult>>(
      `/public/court-hire/${clubSlug}/bookings`,
      payload
    );
    return response.data;
  },

  async cancelGuestBooking(
    clubSlug: string,
    bookingId: string,
    guestEmail: string
  ): Promise<ApiResponse<null>> {
    const response = await publicApiClient.post<ApiResponse<null>>(
      `/public/court-hire/${clubSlug}/bookings/${bookingId}/cancel`,
      { guestEmail }
    );
    return response.data;
  },
};
